import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

/**
 * Paginated search used by the picker/packer Item Default Bin page.
 * Server returns one row per (Item, Warehouse) where a default is configured
 * in a bin-managed warehouse. Supports free-text search on SKU / item name.
 */
export const searchItemDefaultBins = createAsyncThunk(
  "ItemDefaultBin/search",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setActivePayload(payload));

      const filteredPayload = Object.fromEntries(
        Object.entries(payload || {}).filter(
          ([, value]) => value !== null && value !== undefined && value !== ""
        )
      );

      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => params.append(key, item));
        } else {
          params.append(key, value);
        }
      }

      const queryString = params.toString();
      const url = queryString
        ? `${API_URL.ITEM_DEFAULT_BIN.SEARCH}?${queryString}`
        : API_URL.ITEM_DEFAULT_BIN.SEARCH;

      const { data } = await axios.get(url);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/**
 * Read-only preview of a proposed default-bin change. Returns source and
 * destination bin stock so the UI can render the confirmation dialog and
 * let the user choose whether to move stock.
 *
 * Body: { itemId, warehouseId, binId?, newBinName? }
 */
export const previewDefaultBinChange = createAsyncThunk(
  "ItemDefaultBin/previewChange",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        API_URL.ITEM_DEFAULT_BIN.PREVIEW_CHANGE,
        payload
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

/**
 * Commit the default-bin change. When payload.transferStock is true (default),
 * available stock moves from old default bin to new, and Pending picklist advisory
 * BinIds cascade. When false, only the default flag is flipped.
 *
 * Body: { itemId, warehouseId, binId?, newBinName?, transferStock }
 */
export const setItemDefaultBin = createAsyncThunk(
  "ItemDefaultBin/setDefault",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        API_URL.ITEM_DEFAULT_BIN.SET_DEFAULT,
        payload
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

const itemDefaultBinSlice = createSlice({
  name: "itemDefaultBin",
  initialState: {
    item_list: [],
    total_records: 0,
    active_payload: null,
    current_values: null,
    preview_data: null,
    last_result: null,
    loading: false,
    preview_loading: false,
    saving: false,
  },
  reducers: {
    setCurrentValues(state, action) {
      state.current_values = action.payload;
    },
    setActivePayload(state, action) {
      state.active_payload = action.payload;
    },
    clearPreview(state) {
      state.preview_data = null;
    },
    resetCurrentValue(state) {
      state.current_values = null;
      state.preview_data = null;
      state.last_result = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchItemDefaultBins.pending, (state) => {
        state.loading = true;
      })
      .addCase(searchItemDefaultBins.fulfilled, (state, action) => {
        state.item_list = action.payload?.data?.list || [];
        state.total_records = action.payload?.data?.totalRecords || 0;
        state.loading = false;
      })
      .addCase(searchItemDefaultBins.rejected, (state) => {
        state.loading = false;
      })
      .addCase(previewDefaultBinChange.pending, (state) => {
        state.preview_loading = true;
        state.preview_data = null;
      })
      .addCase(previewDefaultBinChange.fulfilled, (state, action) => {
        state.preview_data = action.payload;
        state.preview_loading = false;
      })
      .addCase(previewDefaultBinChange.rejected, (state) => {
        state.preview_loading = false;
      })
      .addCase(setItemDefaultBin.pending, (state) => {
        state.saving = true;
      })
      .addCase(setItemDefaultBin.fulfilled, (state, action) => {
        state.last_result = action.payload;
        state.saving = false;
      })
      .addCase(setItemDefaultBin.rejected, (state) => {
        state.saving = false;
      });
  },
});

export const {
  setCurrentValues,
  setActivePayload,
  clearPreview,
  resetCurrentValue,
} = itemDefaultBinSlice.actions;

export default itemDefaultBinSlice.reducer;
